/**
 * The content lint, as the editor shows it (plan docs/plans/lms-plan.md 8.1
 * "the lint runs on save and blocks publish with the same messages CI would
 * print"; 6.2, 18.11; WP-44): the { rule, path, message } list the server
 * returns with every save, and the one line that says whether anything
 * blocks publishing. The messages are the lint's own words, never rewritten.
 */
import { useId } from 'react';
import { AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { outlineBtnCls } from '../../../../ui/styles';
import type { LintResult } from '../../../api/catalog';
import { noticeCls } from '../../../components/player/styles';
import { PUBLISH_DIALOG_RULES } from './model';
import { cardCls, microLabelCls } from './styles';

export interface LintPanelProps {
  lint: LintResult | null;
  /** Re-run the lint on the saved draft (POST /admin/versions/:id/lint). */
  onRecheck?: () => void;
  checking?: boolean;
}

/** The problems that block the Publish button (the change summary is asked for in the dialog, 8.1). */
export function blockingErrors(lint: LintResult | null) {
  const errors = Array.isArray(lint?.errors) ? lint!.errors : [];
  return errors.filter((e) => !PUBLISH_DIALOG_RULES.has(e.rule));
}

export function LintPanel({ lint, onRecheck, checking }: LintPanelProps) {
  const titleId = useId();
  const errors = Array.isArray(lint?.errors) ? lint!.errors : [];
  const recheck = onRecheck ? (
    <button type="button" className={outlineBtnCls()} onClick={onRecheck} disabled={checking} data-testid="lms-lint-recheck">
      <RefreshCw className={`h-4 w-4 ${checking ? 'animate-spin' : ''}`} aria-hidden="true" />
      Check again
    </button>
  ) : null;

  if (!lint) return null;
  const blocking = blockingErrors(lint);

  if (errors.length === 0) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2" data-testid="lms-lint" data-lint-ok="true">
        <div className={noticeCls('green')} role="status">
          <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>The content lint passes: nothing blocks publishing.</span>
        </div>
        {recheck}
      </div>
    );
  }

  return (
    <section className={`${cardCls} space-y-3`} data-testid="lms-lint" data-lint-ok="false" aria-labelledby={titleId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id={titleId} className="flex items-center gap-2 text-sm font-semibold text-red-700 dark:text-red-400">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          {blocking.length === 0
            ? 'Nothing else blocks publishing: the change summary is asked for when you publish'
            : blocking.length === 1
              ? '1 problem blocks publishing'
              : `${blocking.length} problems block publishing`}
        </h3>
        {recheck}
      </div>
      <ul className="max-h-72 space-y-2 overflow-y-auto pr-1" data-testid="lms-lint-list">
        {errors.map((e, i) => (
          <li
            key={`${e.rule}-${e.path}-${i}`}
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 dark:border-red-800 dark:bg-red-900/20"
            data-testid="lms-lint-item"
            data-rule={e.rule}
          >
            <div className={microLabelCls}>{e.code ? `${e.rule} (${e.code})` : e.rule}</div>
            <p className="text-sm text-gray-900 dark:text-gray-100">{e.message}</p>
            {e.path ? <p className="mt-0.5 break-all font-mono text-xs text-gray-500 dark:text-gray-400">{e.path}</p> : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

export default LintPanel;
